import { uuid } from "uuidv4";
import { Permissions } from "../../models/enums/permissions";
import { BaseService } from "../../services/_base-service";
import { UserCreate } from "../../support/models/users/user-create";
import { UserRead } from "../../support/models/users/user-read";
import { UserSettingEntry, UserSettings, UserSettingsEntryTopic, UserSettingsUpdate } from "../../support/models/users/user-settings";
import { UserUpdate } from "../../support/models/users/user-update";
import { Api, ApiResponse, StatusCode } from "../../utils/api";
import { ApiEndpoints, Routes } from "../../utils/app-constants";
import { navigateToPage } from "../../utils/history";

export class UsersSvc extends BaseService {

    public getUser = async (userId: number) : Promise<ApiResponse<UserRead>> => {
        if (userId) {
            return await this._get(UserRead, userId, ApiEndpoints.USER_USERS_BY_ID);
        }

        return new ApiResponse<UserRead>();
    }

    public testPushNotification = async (sampleText : string, topic : string) => {

        this._showLoading();

        try {

            const userEmail = !topic || topic.length === 0 ? this.store.getState().userdata.user.email : '';
            const pushTopic = !topic || topic.length === 0 ? '' : topic;

            await Api.Post(ApiEndpoints.USER_USERS_NOTIFICATION_TEST, { 
                id: uuid(),
                userId: userEmail,
                topic: pushTopic,
                title: 'Test Push',
                description: sampleText
            });
        }
        catch (err) {
            console.error(err);
        }

        this._hideLoading();

    }

    public getUserNotifications = async () => {

        let response = new ApiResponse<UserSettings>();
        this._showLoading();

        try {
            const result = await Api.Get(UserSettings, ApiEndpoints.USER_USERS_NOTIFICATION_TOPICS);
            
            if (result) {
                response = new ApiResponse<UserSettings>();
                response.status = StatusCode.Success;
                response.data = result;
            }
        }
        catch (err) { 
            response.status = this._handleError(err);
        }

        this._hideLoading();
        return response;
    }

    public updateUserNotifications = async (values : UserSettingEntry[]) => {

        const response = new ApiResponse<boolean>();
        this._showLoading();

        try {

            const subscriptions : string[] = [];

            values.forEach((itm : UserSettingEntry) => {
                itm.topics.forEach((tItm : UserSettingsEntryTopic) => {
                    if (tItm.enabled) {
                        subscriptions.push(tItm.value);
                    }
                });
            });
            
            // the settings are the signed-in user's own, so the API knows whose
            const model = new UserSettingsUpdate();
            model.topics = subscriptions;

            await Api.Put(ApiEndpoints.USER_USERS_NOTIFICATION_UPDATE, model);
            response.status = StatusCode.Success;
            response.data = true;

        }
        catch (err) {
            response.status = this._handleError(err);
        }
        
        this._hideLoading();
        return response;
    }

    public deleteUser = async (userId : number) => {
        const result = await this._delete(ApiEndpoints.USER_USERS_BY_ID.formattify(userId));
        if (result.status === StatusCode.Success) {
            navigateToPage(Routes.SUPPORT_USERS);
        }
    }

    /**
     * Save a new user, or the changes to one
     * @param user The user, as the page has them
     * @param permissions What the user holds, by service center
     */
    public saveUser = async (user : UserRead, permissions: { [serviceCenter: string]: Permissions[] }) => {

        this._showLoading();

        try {
            if (user.id) {
                const u             = new UserUpdate();
                u.permissions       = permissions;
                u.rowVersion        = user.rowVersion;

                await Api.Put(ApiEndpoints.USER_USERS_BY_ID.formattify(user.id), u);
            }
            else {
                const u             = new UserCreate();
                u.email             = user.email;
                u.permissions       = permissions;

                await Api.Post(ApiEndpoints.USER_USERS, u);
            }

            navigateToPage(Routes.SUPPORT_USERS);
        }
        catch (err) {
            this._handleError(err);
        }

        this._hideLoading();
    }

}
